import { Menu, MenuButton, MenuItem, MenuItems, type MenuItemsProps } from "@headlessui/react";
import type { Option } from "@inrev/core";
import { cva, type VariantProps } from "class-variance-authority";
import type { ButtonHTMLAttributes, FC, HTMLAttributes, ReactNode, Ref } from "react";
import { cn } from "../lib/utils";
import { trackingAttrs } from "../utils";

const itemVariants = cva(
	"flex w-full cursor-pointer select-none items-center gap-2 rounded-[4px] px-2.5 py-1.5 text-left text-[13px] font-medium leading-[18px] transition-colors data-[focus]:bg-surface-neutral data-[disabled]:cursor-not-allowed data-[disabled]:opacity-[0.45]",
	{
		variants: {
			tone: {
				default: "text-primary",
				warning: "text-intent-warning-text",
				danger: "text-intent-danger-text",
			},
		},
		defaultVariants: { tone: "default" },
	},
);

type RootProps = Omit<HTMLAttributes<HTMLDivElement>, "children"> & {
	ref?: Ref<HTMLDivElement>;
	/** What the trigger button shows, usually an `Icon`. */
	trigger: ReactNode;
	/** Names the trigger. Required, since the trigger is usually icon-only. */
	triggerAriaLabel: string;
	/** Class names for the trigger button (size, hover reveal). */
	triggerClassName?: string;
	/** Extra `data-*` attributes for the trigger button, such as analytics context beside `trackingId`. */
	triggerDataAttributes?: Option<Record<`data-${string}`, string>>;
	/** Headless floating placement. Portaled, so it escapes `overflow: hidden` ancestors. @default "bottom end" */
	anchor?: MenuItemsProps["anchor"];
	/** Class names for the panel, e.g. a width. */
	menuClassName?: string;
	children: ReactNode;
	trackingId?: string;
	testId?: string;
};

/**
 * A compact action menu for a card's overflow button: rows with an optional tone, dividers,
 * section labels and an inline row of pill buttons. Built on Headless `Menu`, so arrow keys,
 * focus handling and click-outside dismissal come for free, and every row, pills included,
 * closes the menu once picked.
 *
 * Reach for `ButtonDropdown` when the menu hangs off a labeled button, and for `ui/Menu` for
 * free-form popover content.
 */
const Root: FC<RootProps> = ({
	trigger,
	triggerAriaLabel,
	triggerClassName,
	triggerDataAttributes,
	anchor = "bottom end",
	menuClassName,
	children,
	className,
	trackingId,
	testId = "action-menu",
	ref,
	...rest
}) => (
	<Menu as="div" ref={ref} className={cn("relative inline-flex", className)} {...rest}>
		<MenuButton
			aria-label={triggerAriaLabel}
			data-testid={`${testId}-trigger`}
			className={cn(
				"inline-flex cursor-pointer items-center justify-center rounded-[6px] text-tertiary outline-none transition-colors hover:bg-surface-neutral hover:text-primary focus-visible:ring-2 focus-visible:ring-bc-focus data-[open]:bg-surface-neutral data-[open]:text-primary",
				triggerClassName,
			)}
			{...trackingAttrs(trackingId)}
			{...triggerDataAttributes}
		>
			{trigger}
		</MenuButton>
		<MenuItems
			anchor={anchor}
			data-testid={testId}
			className={cn(
				"z-[200000] flex min-w-[184px] flex-col gap-px rounded-[6px] border border-bc-default bg-surface-base p-1 shadow-panel-menu outline-none [--anchor-gap:4px]",
				menuClassName,
			)}
		>
			{children}
		</MenuItems>
	</Menu>
);
Root.displayName = "ActionMenu";

type ItemProps = Omit<ButtonHTMLAttributes<HTMLButtonElement>, "type"> &
	VariantProps<typeof itemVariants> & {
		ref?: Ref<HTMLButtonElement>;
		/** Optional icon before the label. Pair with `<Icon size="mini" />`. */
		leadingIcon?: ReactNode;
		trackingId?: string;
	};

/** A row. Renders a `<button role="menuitem">`; clicking it runs `onClick` and closes the menu. */
const Item: FC<ItemProps> = ({
	tone,
	leadingIcon,
	trackingId,
	className,
	children,
	disabled,
	ref,
	...rest
}) => (
	<MenuItem
		as="button"
		type="button"
		ref={ref}
		disabled={disabled}
		data-tone={tone ?? "default"}
		className={cn(itemVariants({ tone }), className)}
		{...trackingAttrs(trackingId)}
		{...rest}
	>
		{leadingIcon}
		{children}
	</MenuItem>
);
Item.displayName = "ActionMenu.Item";

type DividerProps = HTMLAttributes<HTMLDivElement> & { ref?: Ref<HTMLDivElement> };

/** A hairline between groups of rows. */
const Divider: FC<DividerProps> = ({ className, ref, ...rest }) => (
	<div
		ref={ref}
		role="separator"
		className={cn("-mx-1 my-1 h-px bg-bc-default", className)}
		{...rest}
	/>
);
Divider.displayName = "ActionMenu.Divider";

type LabelProps = HTMLAttributes<HTMLDivElement> & { ref?: Ref<HTMLDivElement> };

/** A small caption above a group, e.g. "Importance". Not focusable. */
const Label: FC<LabelProps> = ({ className, children, ref, ...rest }) => (
	<div
		ref={ref}
		className={cn(
			"px-2.5 pb-1 pt-1.5 text-[11px] font-semibold uppercase leading-4 tracking-[0.06em] text-tertiary",
			className,
		)}
		{...rest}
	>
		{children}
	</div>
);
Label.displayName = "ActionMenu.Label";

type PillRowProps = HTMLAttributes<HTMLDivElement> & { ref?: Ref<HTMLDivElement> };

/** Lays `Pill`s out in one wrapping row. */
const PillRow: FC<PillRowProps> = ({ className, ref, ...rest }) => (
	<div ref={ref} className={cn("flex flex-wrap gap-1 px-2 pb-1.5", className)} {...rest} />
);
PillRow.displayName = "ActionMenu.PillRow";

type PillProps = Omit<ButtonHTMLAttributes<HTMLButtonElement>, "type"> & {
	ref?: Ref<HTMLButtonElement>;
	/** The option currently in effect: dark surface, inverse text. */
	selected?: boolean;
	trackingId?: string;
};

/**
 * A pill button inside a `PillRow`, for picking one value (an importance level). Headless owns
 * `role="menuitem"`, which can't carry `aria-checked`, so the selection reaches screen readers
 * as `sr-only` text in the pill's name.
 */
const Pill: FC<PillProps> = ({
	selected = false,
	trackingId,
	className,
	children,
	ref,
	...rest
}) => (
	<MenuItem
		as="button"
		type="button"
		ref={ref}
		data-selected={selected}
		className={cn(
			"cursor-pointer select-none rounded-full px-2.5 py-0.5 text-[12px] font-medium leading-[18px] transition-colors data-[disabled]:cursor-not-allowed data-[disabled]:opacity-[0.45]",
			selected
				? "bg-surface-primary text-inverse"
				: "bg-surface-neutral text-secondary data-[focus]:bg-surface-dark",
			className,
		)}
		{...trackingAttrs(trackingId)}
		{...rest}
	>
		{children}
		{selected && <span className="sr-only"> Selected</span>}
	</MenuItem>
);
Pill.displayName = "ActionMenu.Pill";

export const ActionMenu = Object.assign(Root, { Item, Divider, Label, PillRow, Pill });

export type ActionMenuProps = RootProps;
export type ActionMenuItemProps = ItemProps;
export type ActionMenuPillProps = PillProps;
